import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import type { ViewDeclV2 } from "@archlang/engine";
import { ActiveViewContext } from "./ActiveViewContext.js";

/** Drop a key from a record state, identity-stable when absent. */
function without<T>(prev: Record<string, T>, key: string): Record<string, T> {
  if (!(key in prev)) return prev;
  const next = { ...prev };
  delete next[key];
  return next;
}

/** Holds the session's active view name (default: none = the identity board). */
export function ActiveViewProvider({ children }: { children: ReactNode }): ReactNode {
  const [activeViewName, setActiveViewName] = useState<string | null>(null);
  const [activePlane, setActivePlane] = useState<string | null>(null);
  // Per-view knob values (same shape discipline as revealsByView below — a
  // session-global map leaked one view's knob into another's same-named knob).
  const [knobsByView, setKnobsByView] = useState<
    Record<string, Record<string, number | string>>
  >({});
  const [draftView, setDraftViewRaw] = useState<ViewDeclV2 | null>(null);
  const setKnob = useCallback((viewKey: string, name: string, value: number | string) => {
    setKnobsByView((prev) => ({
      ...prev,
      [viewKey]: { ...prev[viewKey], [name]: value },
    }));
  }, []);
  // Click-reveal (VIEWS §7): per-view session sets (see ActiveViewApi).
  const [revealsByView, setRevealsByView] = useState<Record<string, readonly string[]>>({});
  const revealKey = useCallback((viewKey: string, qualifiedKey: string) => {
    setRevealsByView((prev) => {
      const cur = prev[viewKey] ?? [];
      if (cur.includes(qualifiedKey)) return prev;
      return { ...prev, [viewKey]: [...cur, qualifiedKey] };
    });
  }, []);
  const clearReveals = useCallback((viewKey: string) => {
    setRevealsByView((prev) => without(prev, viewKey));
  }, []);
  // Draft-session lifecycle for the shared "::draft" slot: knob turns / reveals
  // made while one builder draft projects belong to THAT session only. The
  // boundary is the SESSION KEY (the builder tab id, passed by useViewDrafts):
  // opening, closing, or swapping to another tab's draft drops the slot, so a
  // different view's draft can't inherit the state — while builder edits AND
  // renames within one session (same key) keep it (the create flow recompiles
  // under a new name per name-input keystroke; a name-keyed boundary wiped the
  // session's own state on every keystroke). Callers that pass no key fall
  // back to the draft's name.
  const draftKeyRef = useRef<string | null>(null);
  const setDraftView = useCallback(
    (next: ViewDeclV2 | null, sessionKey?: string | null) => {
      // A NULL draft can still carry a session key: a transient compile
      // failure mid-session (an unparseable intermediate while typing the
      // view name — "onboarding" passes through the keyword "on") projects
      // null without ending the session, and must not wipe its state. A null
      // draft with NO key (builder reset/save, direct callers) ends it.
      const nextKey = sessionKey ?? (next === null ? null : next.name);
      if (draftKeyRef.current !== nextKey) {
        setKnobsByView((prev) => without(prev, "::draft"));
        setRevealsByView((prev) => without(prev, "::draft"));
      }
      draftKeyRef.current = nextKey;
      setDraftViewRaw(next);
    },
    [],
  );
  const api = useMemo(
    () => ({
      activeViewName,
      setActiveViewName,
      activePlane,
      setActivePlane,
      knobsByView,
      setKnob,
      draftView,
      setDraftView,
      revealsByView,
      revealKey,
      clearReveals,
    }),
    [activeViewName, activePlane, knobsByView, setKnob, draftView, revealsByView, revealKey, clearReveals],
  );
  return <ActiveViewContext.Provider value={api}>{children}</ActiveViewContext.Provider>;
}
